import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Stack,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as HeaderQrIcon } from '../assets/icons/header-qr-nav.svg';
import api, { getApiErrorMessage } from '../services/api';
import { useAuth } from '../auth/AuthContext';

interface StoreQRResponse {
  qr_code_id: string;
  qr_string: string;
  qr_image_svg_base64: string;
  valid_until: string;
  created_at?: string;
}

const AttendanceQRPage: React.FC = () => {
  const { storeId } = useAuth(); // 認証コンテキストから店舗ID取得

  const [qrData, setQrData] = useState<StoreQRResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStoreQR = useCallback(async () => {
    if (!storeId) {
      setError('店舗IDが見つかりません');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // まず既存のQRを取得
      const data = await api.attendance.getStoreQR(storeId);
      setQrData(data);
    } catch (err: any) {
      // QRが存在しない場合は新規生成
      if (err.response?.status === 404) {
        try {
          const data = await api.attendance.generateStoreQR(storeId, 365);
          setQrData(data);
        } catch (createErr: any) {
          setError(getApiErrorMessage(createErr, 'QRコード生成に失敗しました'));
        }
      } else {
        setError(getApiErrorMessage(err, 'QRコード取得に失敗しました'));
      }
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchStoreQR();
  }, [fetchStoreQR]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PageHeader
        title="出退勤QR"
        icon={<SvgIcon component={HeaderQrIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* メインコンテンツ */}
      <Container maxWidth="sm" sx={{ pb: { xs: 3, sm: 4 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {qrData && (
          <Stack spacing={3}>
            {/* QRコード表示 - 中央配置、大きく表示 */}
            <Card
              elevation={0}
              sx={{
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3} alignItems="center">
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '320px',
                      aspectRatio: '1',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'white'
                    }}
                  >
                    <img
                      src={`data:image/svg+xml;base64,${qrData.qr_image_svg_base64}`}
                      alt="Store QR Code"
                      style={{ width: '100%', height: '100%', display: 'block' }}
                    />
                  </Box>

                  {/* 情報表示 */}
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      QRコードID
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" gutterBottom sx={{ mb: 2 }}>
                      {qrData.qr_code_id}
                    </Typography>

                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* アクションボタン - フルワイド、大きく */}
            <Stack spacing={2}>
<Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{ py: 1.5 }}
              >
                印刷する
              </Button>
            </Stack>

            {/* 使用方法 */}
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                <strong>使用方法</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1, lineHeight: 1.8 }}>
                1. クルーがスマホでQRスキャン<br />
                2. 出勤・退勤時にそれぞれ読み取り<br />
                3. マネージャーが承認画面で最終確認
              </Typography>
            </Alert>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default AttendanceQRPage;
